import { useQuery } from "@tanstack/react-query";
import { getTokenAsync } from "@coworker/app/src/hooks/API/getAuthToken";
import { useStoreId } from "@coworker/app/src/core/auth/useLoggedInUser";
import {
  ATKOMST_BASIC_PERMISSION,
  ATKOMST_TEAMS_EDITOR_PERMISSION,
} from "@coworker/apprestructured/src/shared/types/atkomstPermissions";

const atkomstUrl = "https://api.ingka.ikea.com/auth-service";
const ATKOMST_USER_HAS_NO_PERMISSION = 403;

const normalizeStoreIdForAtkomst = (storeId: string) =>
  storeId.split("#")[0] ?? storeId;

const checkPermissionOnNode = async (
  token: string,
  permissionName: string,
  nodeRef: string
): Promise<boolean> => {
  const query = new URLSearchParams({
    service: "FIXA",
    permissionName,
    clientExternalReference: nodeRef,
  });
  const url = `${atkomstUrl}/authorize?${query.toString()}`;
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  if (response.status === ATKOMST_USER_HAS_NO_PERMISSION) return false;
  if (!response.ok) {
    throw new Error(
      `Atkomst authorize fetch failed: ${response.status} ${response.statusText}`
    );
  }
  return true;
};

const getUserPermissionsForStore = async (
  storeId: string
): Promise<string[]> => {
  const token = await getTokenAsync();
  if (!token) {
    throw new Error("No token available");
  }

  const nodeRef = `STO.${storeId}`;
  const [hasBasic, hasTeamsEditor] = await Promise.all([
    checkPermissionOnNode(token, ATKOMST_BASIC_PERMISSION, nodeRef),
    checkPermissionOnNode(token, ATKOMST_TEAMS_EDITOR_PERMISSION, nodeRef),
  ]);

  const permissions: string[] = [];
  if (hasBasic) permissions.push(ATKOMST_BASIC_PERMISSION);
  if (hasTeamsEditor) permissions.push(ATKOMST_TEAMS_EDITOR_PERMISSION);
  return permissions;
};

const useUserAtkomstPermissions = (storeId?: string, enabled = true) => {
  const loggedInStoreId = useStoreId();
  const requestedStoreId = storeId || loggedInStoreId;
  const atkomstStoreId = requestedStoreId
    ? normalizeStoreIdForAtkomst(requestedStoreId)
    : "";

  return useQuery({
    queryKey: ["userAtkomstRoles", atkomstStoreId],
    queryFn: () => getUserPermissionsForStore(atkomstStoreId),
    enabled: enabled && !!atkomstStoreId,
    staleTime: 60 * 60 * 1000 * 24, // 1 day
    retry: false,
  });
};

export default useUserAtkomstPermissions;
